import { useState, type ReactNode } from 'react';

import { Box, Card, Chip, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { radius } from '@karehero/llama/theme/radius';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import PushScreen from '@/designs-dev/device/PushScreen';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import { RECIPIENT } from '@/karehero-app/tell-us-once/intake/content';
import { CARE_HUB } from '@/karehero-app/care/CareHub.content';
import {
  SHORTLISTS,
  type ShortlistProvider,
} from '@/karehero-app/care/shortlists/fixtures';
import {
  HUB_SHORTLISTS as COPY,
  hubDate,
  hubPrice,
} from '@/karehero-app/care/HubShortlists.content';

/** The pushed page's bar, drawn by the hub so every page's bar matches. */
type Bar = (title: string, close: () => void) => ReactNode;

/** Harness-only: open straight onto a shortlist, a provider or the booking. */
export type HubShortlistsInitial = {
  shortlistId?: string;
  providerId?: string;
  booking?: boolean;
};

const page = (children: ReactNode) => (
  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2, pb: 4 }}>
    <Stack spacing={1.5}>{children}</Stack>
  </Box>
);

const callRow = (description: string) => (
  <ClaimRow icon='phone' name={CARE_HUB.lead.call} description={description} />
);

// A provider on a shortlist: a picture, its name, where and how much, and
// where things are with it. The care expert's note and the rest are on its
// page, so the list stays a list.
const ProviderRow = ({
  provider,
  onOpen,
}: {
  provider: ShortlistProvider;
  onOpen: () => void;
}) => {
  const status = COPY.status[provider.status];
  return (
    <Card
      onClick={onOpen}
      sx={{
        p: 2,
        cursor: 'pointer',
        opacity: provider.status === 'Unavailable' ? 0.6 : 1,
      }}
    >
      <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            flexShrink: 0,
            borderRadius: `${radius.md}px`,
            bgcolor: 'custom.neutral.200',
          }}
        />
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
          <Typography variant='title'>{provider.name}</Typography>
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            {provider.distance}, {COPY.provider.from(hubPrice(provider.price))}
          </Typography>
          {status && (
            <Box sx={{ alignSelf: 'flex-start', pt: 0.5 }}>
              <Chip size='small' label={status} />
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Stack direction='row' spacing={2} sx={{ justifyContent: 'space-between' }}>
    <Typography variant='body1' sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant='body1' sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

/**
 * CONCEPT. The shortlists a care expert has made for the person picked, as
 * the Care hub opens them: the lists, a list, a provider, each a pushed page.
 */
const HubShortlists = ({
  who,
  bar,
  onClose,
  initial,
}: {
  who: string;
  bar: Bar;
  onClose: () => void;
  initial?: HubShortlistsInitial;
}) => {
  // The fixtures are Margaret's; nobody else has a shortlist yet.
  const lists = who === RECIPIENT ? SHORTLISTS : [];
  const [listId, setListId] = useState(initial?.shortlistId);
  const [providerId, setProviderId] = useState(initial?.providerId);
  const [booking, setBooking] = useState(Boolean(initial?.booking));
  const list = lists.find((s) => s.id === listId);
  const found = list?.providers.find((p) => p.id === providerId);
  const provider = found;
  // The fixtures' notes are written for the master, where it's Mum.
  const note = provider?.note?.replace(/\bMum\b/g, who);

  return (
    <>
      <PushScreen
        onClose={onClose}
        bar={(close) => bar(COPY.list.title, close)}
      >
        {() =>
          page(
            lists.length === 0 ? (
              callRow(COPY.list.none(who))
            ) : (
              <>
                {lists.map((s) => (
                  <ClaimRow
                    key={s.id}
                    name={s.name}
                    description={
                      s.providers.length === 0
                        ? COPY.list.waiting
                        : COPY.list.count(
                            s.providers.length,
                            hubDate(s.updated),
                          )
                    }
                    onClick={() => setListId(s.id)}
                  />
                ))}
                {callRow(COPY.list.call)}
              </>
            ),
          )
        }
      </PushScreen>

      {list && (
        <PushScreen
          onClose={() => setListId(undefined)}
          bar={(close) => bar(list.name, close)}
        >
          {() =>
            page(
              <>
                {list.providers.length === 0 ? (
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    {COPY.shortlist.waiting}
                  </Typography>
                ) : (
                  list.providers.map((p) => (
                    <ProviderRow
                      key={p.id}
                      provider={p}
                      onOpen={() => setProviderId(p.id)}
                    />
                  ))
                )}
                {callRow(COPY.list.call)}
              </>,
            )
          }
        </PushScreen>
      )}

      {provider && (
        <PushScreen
          onClose={() => setProviderId(undefined)}
          bar={(close) => bar(provider.name, close)}
        >
          {() =>
            page(
              <>
                <Box
                  sx={{
                    height: 160,
                    borderRadius: `${radius.md}px`,
                    bgcolor: 'custom.neutral.200',
                  }}
                />
                <Stack spacing={0.5}>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    {provider.address}, {provider.distance}
                  </Typography>
                  {COPY.status[provider.status] && (
                    <Box sx={{ alignSelf: 'flex-start', pt: 0.5 }}>
                      <Chip size='small' label={COPY.status[provider.status]} />
                    </Box>
                  )}
                </Stack>
                {note && (
                  <Card sx={{ p: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography variant='title'>
                        {COPY.provider.note}
                      </Typography>
                      <Typography variant='body1'>{note}</Typography>
                    </Stack>
                  </Card>
                )}
                <Card sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Fact
                      label={COPY.provider.facts.rating}
                      value={provider.rating}
                    />
                    <Fact
                      label={COPY.provider.facts.price}
                      value={hubPrice(provider.price)}
                    />
                    <Fact
                      label={COPY.provider.facts.space}
                      value={provider.availability}
                    />
                    <Fact
                      label={COPY.provider.facts.visiting}
                      value={provider.visiting}
                    />
                  </Stack>
                </Card>
                <Stack spacing={1}>
                  <Typography variant='title'>{COPY.provider.care}</Typography>
                  <Stack
                    direction='row'
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {provider.careTypes.map((c) => (
                      <Chip key={c} size='small' label={c} />
                    ))}
                  </Stack>
                </Stack>
                {provider.status !== 'Unavailable' && (
                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant='primary'
                      size='lg'
                      isFullWidth
                      ariaLabel={COPY.provider.visit}
                      onPress={() => setBooking(true)}
                    >
                      {COPY.provider.visit}
                    </Button>
                  </Box>
                )}
              </>,
            )
          }
        </PushScreen>
      )}

      {/* A short choice, so a sheet over the page rather than another page. */}
      {provider && booking && (
        <BottomSheet
          variant='hug'
          title={COPY.provider.visit}
          onClose={() => setBooking(false)}
        >
          <Stack spacing={2} sx={{ pb: 1 }}>
            <Button
              variant='secondary'
              size='lg'
              isFullWidth
              iconLeft={'phone' as never}
              ariaLabel={COPY.provider.call(provider.phone)}
            >
              {COPY.provider.call(provider.phone)}
            </Button>
            <Button
              variant='secondary'
              size='lg'
              isFullWidth
              iconLeft={'envelope' as never}
              ariaLabel={COPY.provider.email}
            >
              {COPY.provider.email}
            </Button>
          </Stack>
        </BottomSheet>
      )}
    </>
  );
};

export default HubShortlists;
